import { ReactNode, useEffect } from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { OidcProvider } from 'redux-oidc';
import { Backdrop } from '@material-ui/core';
import store, { useAppDispatch, useAppSelector } from '../../store';
import { getAdviserDetailsAsync, getAdvisersAsync, getDefaultDisclosureTemplateAsync } from '../../store/adviser';
import { portUserManager as userManager } from '../../api';
import { Callback, Signout } from '../authentication';
import AdviserScreen from '../containers/AdviserScreen';
import Layout from '../containers/Layout';
import { adviserProductRoles, supportRoles } from '../../common/types';
import { LoadingAnimation, ScrollToTop } from '../controls/helpers';
import DisclosureTemplate from './disclosureTemplate';
import KiwiSaverCalculator from './tools/KiwiSaverCalculator';
import KiwiSaverRetentionTool from './tools/KiwiSaverRetentionTool';
import NewApplication from './newApplication';
import OnboardingStatus from './onboardingStatus';
import SavingsAdviceTool from './tools/SavingsAdviceTool';
import NewSatApplication from './satApplication/steps/NewSatApplication';
import SatApplication from './satApplication';
import AdviceConfirmation from './adviceConfirmation';
import ClientApplication from './clientApplication';
import Sat from '../client/savingsAdviceTool';
import { getRoles } from './Helpers';

export default ({ history, location, match }: RouteComponentProps) => {
    const path = match.path;
    const dispatch = useAppDispatch();
    const { user, isLoadingUser } = useAppSelector(state => state.oidc);
    const isAuthenticated = isLoadingUser || user?.expired === false || location.pathname === `${path}/authentication/callback` || location.pathname === `${path}/authentication/signout`;

    useEffect(() => {
        if (!isAuthenticated) userManager.signinRedirect({ state: location.pathname });
        else if (user) {
            const roles = getRoles(user);
            if (!roles.some(role => adviserProductRoles.includes(role)) && !roles.some(role => supportRoles.includes(role))) history.push('/adviser/authentication/signout');
            dispatch(getAdviserDetailsAsync());
            dispatch(getDefaultDisclosureTemplateAsync());
            dispatch(getAdvisersAsync());
        }
    }, [isAuthenticated, user]);

    return <OidcProvider store={store} userManager={userManager}>
        <Layout>
            {!isAuthenticated ?
                <Backdrop open style={{ zIndex: 9999 }}>
                    <LoadingAnimation />
                </Backdrop>
                : <Switch>
                    <AdviserLayoutRoute path={`${path}/authentication/callback`} render={routeProps => <Callback {...routeProps} userManager={userManager} />} />
                    <AdviserLayoutRoute path={`${path}/authentication/signout`} render={_ => <Signout userManager={userManager} />} />
                    <AdviserLayoutRoute path={`${path}/new-application`} render={routeProps => <NewApplication {...routeProps} />} />
                    <Route path={`${path}/new-sat-application/:products`} render={routeProps => <NewSatApplication {...routeProps} />} />
                    <Route path={`${path}/sat/:clientId`} render={routeProps => <Sat {...routeProps} />} />
                    <Route path={`${path}/sat-application/:clientId`} render={routeProps => <SatApplication {...routeProps} />} />
                    <AdviserLayoutRoute path={`${path}/onboarding-status`} render={routeProps => <OnboardingStatus {...routeProps} />} />
                    <AdviserLayoutRoute path={`${path}/disclosure-template`} render={routeProps => <DisclosureTemplate {...routeProps} />} />
                    <AdviserLayoutRoute path={`${path}/savings-advice-tool`} render={_ => <SavingsAdviceTool />} />
                    <AdviserLayoutRoute path={`${path}/kiwisaver-calculator`} render={_ => <KiwiSaverCalculator />} />
                    <AdviserLayoutRoute path={[`${path}/kiwisaver-retention-tool`, `${path}/:clientId/kiwisaver-retention-tool`]} render={routeProps => <KiwiSaverRetentionTool {...routeProps} />} />
                    <AdviserLayoutRoute path={`${path}/:clientId/client-application`} render={routeProps => <ClientApplication {...routeProps} />} />
                    <AdviserLayoutRoute path={`${path}/:clientId/advice-confirmation`} render={routeProps => <AdviceConfirmation {...routeProps} />} />
                    <Route>
                        <Redirect to={`${path}/new-application`} />
                    </Route>
                </Switch>
            }
        </Layout>
    </OidcProvider>
}

const AdviserLayoutRoute = (props: { path: string | string[]; render: (props: RouteComponentProps) => ReactNode; }) =>
    <AdviserScreen>
        <ScrollToTop />
        <Route {...props} />
    </AdviserScreen>
